import SingleReading from "@/components/single-reading"
import { Button } from "@/components/ui/button"
import { ErrorFlagState } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Icon } from "@/icons/icon"
import { useFieldError, UseFormReturn, useWatch } from "@/lib/form/form"
import { ParamSchema } from "@/lib/form/schema"
import { cn, EnumsOf } from "@/lib/utils"
import { createLimitValidator } from "@/lib/validators/limit"
import { useEffect, useState } from "react"

const State = {
  Base: "Base",
  Remarks: "Remarks",
} as const
type State = EnumsOf<typeof State>

interface ResampleFormProps {
  path: string
  schema: ParamSchema
  className?: string
  disabled?: boolean
  form: UseFormReturn
}

export default function ResampleForm({
  schema,
  path,
  form,
  className,
  disabled,
}: ResampleFormProps) {
  const [state, setState] = useState<State>(State.Base)
  const [isResampling, setIsResampling] = useState<boolean>(false)
  const resampled = useWatch({
    form,
    name: path,
    deep: 1,
  })
  const error = useFieldError({
    form,
    names: [path],
  })[0]

  useInit()

  function useInit() {
    useEffect(() => {
      form.register(path, {
        validate: createLimitValidator({
          schema,
          path,
          form,
        }),
        value: resampled,
      })

      if (resampled?.value) {
        setIsResampling(true)
      } else {
        setIsResampling(false)
      }

      if (resampled?.remark) {
        setState(State.Remarks)
      }
    }, [])

    useEffect(() => {
      if (!open) {
        setTimeout(() => {
          setIsResampling(false)
          form.reset()
        }, 500)
      }
    }, [open])

    useEffect(() => {
      if (!resampled?.value) {
        setState(State.Base)
        return
      }

      if (error) {
        setState(State.Remarks)
      } else if (!error && resampled?.remark) {
        setState(State.Remarks)
      } else {
        setState(State.Base)
      }
    }, [error, resampled])
  }

  function getLabel() {
    return `${schema.name}(${schema.unit})`
  }

  function getLabelHelpText() {
    if (!schema.limit) {
      return ""
    }

    return `${schema.limit.min} - ${schema.limit.max}`
  }

  function getFlagState(): ErrorFlagState {
    if (!resampled?.value) {
      return ErrorFlagState.None
    }

    if (error) {
      if (resampled?.remark) {
        return ErrorFlagState.Filled
      }

      return ErrorFlagState.Unfilled
    } else {
      if (resampled?.remark) {
        return ErrorFlagState.Filled
      } else {
        return ErrorFlagState.None
      }
    }
  }

  function handleClickResample() {
    setIsResampling(true)
  }

  function isChecked() {
    return !error && !!resampled?.value && !resampled?.remark
  }

  function getResampleClearedPath() {
    return `${path}Cleared`
  }

  function handleChangeResampledValue(value: string) {
    form.setValue(`${path}.value`, value, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    })
    form.setValue(`${path}.recordedAt`, new Date().toISOString(), {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    })
    form.setValue(getResampleClearedPath(), false, {
      shouldValidate: false,
      shouldDirty: true,
      shouldTouch: true,
    })

    if (!value) {
      form.setValue(`${path}.remark`, undefined)
    }
  }

  function handleChangeRemarks(value: string) {
    form.setValue(`${path}.remark`, value, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    })
    form.setValue(`${path}.recordedAt`, new Date().toISOString(), {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    })
    form.setValue(getResampleClearedPath(), false, {
      shouldValidate: false,
      shouldDirty: true,
      shouldTouch: true,
    })
  }

  function handleClickDelete() {
    setIsResampling(false)
    form.setValue(path, undefined)
    form.setValue(getResampleClearedPath(), true, {
      shouldValidate: false,
      shouldDirty: true,
      shouldTouch: true,
    })
  }

  return (
    <div className={cn(className)}>
      {!isResampling && (
        <button
          className="flex items-center gap-2.5 text-blue hover:cursor-pointer"
          type="button"
        >
          <Icon name="PlusFilled" className="h-5 w-auto" />
          <span className="text-sm font-semibold" onClick={handleClickResample}>
            Resample
          </span>
        </button>
      )}

      {isResampling && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-label-text text-sm font-medium">
              Resample Value
            </h3>

            <Button variant="ghost" onClick={handleClickDelete}>
              <Icon name="TrashCan" className="h-5 w-auto text-icon" />
            </Button>
          </div>

          <div className="rounded-sm bg-box-l-border p-3">
            <SingleReading
              label={getLabel()}
              labelHelpText={getLabelHelpText()}
              inputMode="numeric"
              isChecked={isChecked()}
              error={error || ""}
              flagState={getFlagState()}
              value={resampled?.value}
              disabled={disabled}
              onChange={handleChangeResampledValue}
            />

            {state === State.Remarks && (
              <Textarea
                className="mt-4 w-full min-h-22"
                placeholder="Describe the issue and action taken"
                value={resampled?.remark}
                disabled={disabled}
                onChange={handleChangeRemarks}
              />
            )}
          </div>
        </div>
      )}
    </div>
  )
}
